import { Component, TemplateRef, inject, viewChild } from '@angular/core';

import { DialogHandlerComponent } from '../../shared/dialog-handler/dialog-handler.component';

import { FleetService } from '../../services/fleet.service';
import { ShipService } from '../../services/ship.service';

import { Fleet } from '../../interfaces/fleet';
import { Ship } from '../../interfaces/ship';

@Component({
  selector: 'ism-split-fleet',
  standalone: false,
  templateUrl: './split-fleet.component.html'
})
export class SplitFleetComponent extends DialogHandlerComponent {
  shipService = inject(ShipService);
  fleetService = inject(FleetService);
  starSystemSelect = viewChild.required<TemplateRef<any>>('starSystemId');

  title: string = 'Split Fleet';
  state: string = 'ready';
  baseFleet: Fleet;
  shipIds: string[] = [];
  baseShipIds: string[] = [];
  ships: Ship[];
  shipById: { [key: string]: Ship; } = {};
  hullClasses = new Set<string>();
  shipClasses = new Set<string>();
  speeds = new Set<number>();
  damaged: string[] = [];
  newFleets: Fleet[] = [];
  tempSpeeds: number[];
  selectedFleet: Fleet;

  constructor () {
    super();

    this.baseFleet = Object.assign({}, this.data.document as Fleet);
    this.title = this.title + ' - ' + this.baseFleet.fleetName;
    this.shipIds = Object.assign([], this.baseFleet.shipIds);
    this.baseFleet.shipIds = this.shipIds;
    this.baseShipIds = Object.assign([], this.baseFleet.shipIds);
    this.ships = Object.assign([], this.baseFleet.ships);
    this.ships.forEach(ship => {
      this.shipById[ship._id] = ship;
      let counts = this.shipService.countItems(ship, ['engines']);
      ship['counts'] = counts;
      this.speeds.add(counts['speed']);
      this.hullClasses.add(ship.hullClass.toUpperCase());
      this.shipClasses.add(ship.shipClassId);
      if (ship?.damageOverlay && ship?.damageOverlay.indexOf('/') > -1) {
        this.damaged.push(ship._id);
      }
    });
  };

  newFleet (fleetName: string, shipIds: string[]): Fleet {
    let newFleet = this.fleetService.getNewFleet(this.baseFleet.campaignId, this.baseFleet.raceId, this.baseFleet.locationHex);
    newFleet.fleetName = fleetName;
    newFleet.shipIds = shipIds;
    newFleet.starSystemId = this.baseFleet.starSystemId;
    newFleet.turnActivated = this.session.campaign.turn;
    return newFleet;
  };

  splitBySpeed () {
    this.state = 'speed';
    this.newFleets = [];
    let shipsGroupedBySpeed = this.shipIds.reduce((group, shipId) => {
      let speed = this.shipById[shipId]['counts']['speed'] || 'unknown';

      let tempArray = group[speed] || [];
      tempArray.push(shipId);
      group[speed] = tempArray;

      return group;
    }, {});
    let speedArray = Array.from(this.speeds);
    speedArray.forEach((speed, index) => {
      if (index === 0) {
        this.baseFleet.shipIds = shipsGroupedBySpeed[speed];
      }
      else {
        let newFleet = this.newFleet(this.baseFleet.fleetName + '-' + speed, shipsGroupedBySpeed[speed]);
        this.newFleets.push(newFleet);
      }
    });
  };

  splitByHullClass () {
    this.state = 'class';
    this.newFleets = [];
    let shipsGroupedByClass = this.shipIds.reduce((group, shipId) => {
      let hullClass = this.shipById[shipId].hullClass;

      let tempArray = group[hullClass] || [];
      tempArray.push(shipId);
      group[hullClass] = tempArray;

      return group;
    }, {});
    let hullClassArray = Array.from(this.hullClasses);
    hullClassArray.forEach((hullClass, index) => {
      if (index == 0) {
        this.baseFleet.shipIds = shipsGroupedByClass[hullClass];
      }
      else {
        let newFleet = this.newFleet(this.baseFleet.fleetName + '-' + hullClass, shipsGroupedByClass[hullClass]);
        this.newFleets.push(newFleet);
      }
    });
  };

  splitByShipClass () {
    this.state = 'class';
    this.newFleets = [];
    let shipsGroupedByClass = this.shipIds.reduce((group, shipId) => {
      let shipClass = this.shipById[shipId].shipClassId;

      let tempArray = group[shipClass] || [];
      tempArray.push(shipId);
      group[shipClass] = tempArray;

      return group;
    }, {});
    let shipClassArray = Array.from(this.shipClasses);
    shipClassArray.forEach((shipClass, index) => {
      if (index == 0) {
        this.baseFleet.shipIds = shipsGroupedByClass[shipClass];
      }
      else {
        let newFleet = this.newFleet(this.baseFleet.fleetName + '-SC-' + index, shipsGroupedByClass[shipClass]);
        this.newFleets.push(newFleet);
      }
    });
  };

  splitByDamage () {
    this.state = 'damage';
    this.newFleets = [
      this.newFleet(this.baseFleet.fleetName + '-Damaged', this.damaged)
    ];
  };

  addManualFleet () {
    let num = this.newFleets.length + 1;
    let theNewFleet = this.newFleet("New-" + num, []);
    this.selectedFleet = theNewFleet;
    this.newFleets.push(theNewFleet);
  };

  splitManually () {
    this.state = 'manual';
    this.addManualFleet();
  };

  cancelSplit () {
    this.state = 'ready';
    this.newFleets = [];
    this.selectedFleet = null;
    this.baseFleet.shipIds = this.baseShipIds;
  };

  moveShip (fleet: Fleet, shipId: string) {
    if (this.state !== "ready" && this.selectedFleet != null) {
      fleet.shipIds.splice(fleet.shipIds.indexOf(shipId), 1);
      if (fleet === this.selectedFleet) {
        this.baseFleet.shipIds.push(shipId);
      }
      else {
        this.selectedFleet.shipIds.push(shipId);
      }
    }
  };

  override save () {
    this.dialogRef.close({
      documentName: "Fleet",
      document: [this.baseFleet, ...this.newFleets]
    });
  };
};
